import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/api.service';
import { Employee } from '../model/employee.model';
import { CustomerBills } from '../model/customerbills.model';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface CycleMetrics {
  totalCustomers: number;
  totalBilledCustomers: number;
  totalBillAmount: number;
  totalPaid: number;
  totalRh: number;
  totalWadarta: number;
  totalRc: number;
  totalBadDebt: number;
  totalDiscount: number;
}

export interface CycleData {
  label: string;
  shortLabel: string;
  startDate: string;
  endDate: string;
  summary: CycleMetrics;
  byEmployee: Map<number, CycleMetrics>;
}

export interface MetricConfig {
  key: keyof CycleMetrics;
  label: string;
  color: string;
  type: 'count' | 'currency';
  selected: boolean;
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  type: 'count' | 'currency';
  points: { x: number; y: number; value: number; cycleLabel: string }[];
  path: string;
  areaPath: string;
}

@Component({
  selector: 'app-billing-cycle-graph',
  templateUrl: './billing-cycle-graph.component.html',
  styleUrls: ['./billing-cycle-graph.component.scss']
})
export class BillingCycleGraphComponent implements OnInit {
  loading = false;
  loadingProgress = 0;

  // View mode
  viewMode: 'summary' | 'employee' = 'summary';

  // Cycle range
  cycleCountOptions = [3, 6, 9, 12];
  selectedCycleCount = 6;

  // Metrics
  metrics: MetricConfig[] = [
    { key: 'totalCustomers', label: 'Total Customers', color: '#7bd8ff', type: 'count', selected: false },
    { key: 'totalBilledCustomers', label: 'Billed Customers', color: '#b085ff', type: 'count', selected: false },
    { key: 'totalBillAmount', label: 'Bill Amount', color: '#ffd700', type: 'currency', selected: true },
    { key: 'totalPaid', label: 'Paid', color: '#3ddc84', type: 'currency', selected: true },
    { key: 'totalRh', label: 'RH', color: '#ff9d5b', type: 'currency', selected: false },
    { key: 'totalWadarta', label: 'Wadarta', color: '#00ffc8', type: 'currency', selected: false },
    { key: 'totalRc', label: 'RC', color: '#ff5b8f', type: 'currency', selected: true },
    { key: 'totalBadDebt', label: 'Daan Guud', color: '#ff5b5b', type: 'currency', selected: false },
    { key: 'totalDiscount', label: 'Discount', color: '#ffc857', type: 'currency', selected: false }
  ];

  // Employees
  collectorEmployees: Employee[] = [];
  selectedEmployeeId: number | null = null;
  employeeSearchTerm = '';

  // Loaded data
  cycles: CycleData[] = [];
  dataLoaded = false;

  // Chart
  chartWidth = 1080;
  chartHeight = 420;
  chartPadding = { top: 30, right: 40, bottom: 56, left: 90 };
  chartSeries: ChartSeries[] = [];
  yTicks: { y: number; label: string }[] = [];
  xTicks: { x: number; label: string }[] = [];
  hoverIndex: number | null = null;
  hoverX = 0;

  // Chart type
  chartType: 'line' | 'bar' = 'line';
  barGroups: { x: number; cycleLabel: string; bars: { x: number; y: number; height: number; width: number; color: string; label: string; value: number; type: 'count' | 'currency' }[] }[] = [];

  constructor(
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  // ---------- cycle generation (same rules as billing dashboard: 25th -> 24th) ----------
  private generateCycles(count: number): { label: string; shortLabel: string; startDate: string; endDate: string }[] {
    const today = new Date();
    const cycles: { label: string; shortLabel: string; startDate: string; endDate: string }[] = [];
    const seen = new Set<string>();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    for (let i = 0; cycles.length < count && i < count + 3; i++) {
      const ref = new Date(today.getFullYear(), today.getMonth() - i, 15);
      let start = new Date(ref.getFullYear(), ref.getMonth(), 25);
      let end = new Date(ref.getFullYear(), ref.getMonth() + 1, 24);
      if (start > today) {
        start.setMonth(start.getMonth() - 1);
        end.setMonth(end.getMonth() - 1);
      }
      const label = `${monthNames[start.getMonth()]} ${start.getFullYear()} Cycle`;
      if (seen.has(label)) { continue; }
      seen.add(label);
      cycles.push({
        label,
        shortLabel: `${monthNames[start.getMonth()]} ${String(start.getFullYear()).slice(2)}`,
        startDate: this.formatDate(start),
        endDate: this.formatDate(end)
      });
    }
    return cycles.reverse(); // oldest first for the chart
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // ---------- data loading ----------
  onCycleCountChange(count: number): void {
    this.selectedCycleCount = count;
    this.loadData();
  }

  refresh(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loading = true;
    this.loadingProgress = 0;
    this.dataLoaded = false;
    this.cycles = [];

    const cycleDefs = this.generateCycles(this.selectedCycleCount);

    // Single backend call: per-collector bills for all cycles at once
    this.apiService.getBillingGraphData(cycleDefs).subscribe({
      next: (res: any) => {
        const returnedCycles = res?.cycles || [];

        // Build collector employee list from the first returned cycle
        const firstCycle = returnedCycles[0];
        const firstCollectors = firstCycle?.collectors || [];
        this.collectorEmployees = firstCollectors
          .filter((c: any) => c.employeeId != null && c.employeeName)
          .map((c: any) => ({
            employeeId: c.employeeId,
            employeeName: c.employeeName,
            roles: [{ roleName: 'collector' }]
          }))
          .sort((a: Employee, b: Employee) => (a.employeeName || '').localeCompare(b.employeeName || ''));
        if (this.selectedEmployeeId === null && this.collectorEmployees.length > 0) {
          this.selectedEmployeeId = this.collectorEmployees[0].employeeId;
        }

        returnedCycles.forEach((cycle: any) => {
          const def = cycleDefs.find(d =>
            d.startDate === cycle.startDate && d.endDate === cycle.endDate
          ) || { label: cycle.startDate, shortLabel: cycle.startDate };

          const byEmployee = new Map<number, CycleMetrics>();
          const summary: CycleMetrics = this.emptyMetrics();
          const collectors = cycle?.collectors || [];

          collectors.forEach((c: any) => {
            const bills: CustomerBills[] = c.customerBills || [];

            const hasBillCreated = bills.some(b => b.billDate && b.billDate !== null);
            const hasRHAmount = bills.some(b => b.prevMonthOutstandingBal !== null && b.prevMonthOutstandingBal !== 0);
            if (!hasBillCreated && !hasRHAmount) { return; }

            const m = this.computeMetrics(bills);
            byEmployee.set(c.employeeId, m);

            summary.totalCustomers += m.totalCustomers;
            summary.totalBilledCustomers += m.totalBilledCustomers;
            summary.totalBillAmount += m.totalBillAmount;
            summary.totalPaid += m.totalPaid;
            summary.totalRh += m.totalRh;
            summary.totalWadarta += m.totalWadarta;
            summary.totalRc += m.totalRc;
            summary.totalBadDebt += m.totalBadDebt;
            summary.totalDiscount += m.totalDiscount;
          });

          this.cycles.push({
            label: def.label,
            shortLabel: def.shortLabel,
            startDate: cycle.startDate,
            endDate: cycle.endDate,
            summary,
            byEmployee
          });
        });

        this.loading = false;
        this.loadingProgress = 100;
        this.dataLoaded = true;
        this.buildChart();
      },
      error: (err) => {
        console.error('Error loading graph data:', err);
        this.loading = false;
        this.loadingProgress = 0;
        this.snackBar.open('Error loading graph data', 'Close', { duration: 3000 });
      }
    });
  }

  private emptyMetrics(): CycleMetrics {
    return {
      totalCustomers: 0,
      totalBilledCustomers: 0,
      totalBillAmount: 0,
      totalPaid: 0,
      totalRh: 0,
      totalWadarta: 0,
      totalRc: 0,
      totalBadDebt: 0,
      totalDiscount: 0
    };
  }

  // Same formulas as the Billing Dashboard so figures match exactly
  private computeMetrics(bills: CustomerBills[]): CycleMetrics {
    const totalBillAmount = bills.reduce((sum, b) => sum + (b.billAmount || 0), 0);
    const totalPaid = bills.reduce((sum, b) => sum + (b.amountPaid || 0), 0);
    const totalRh = bills.reduce((sum, b) => sum + (b.prevMonthOutstandingBal || 0), 0);
    const totalWadarta = bills.reduce((sum, b) => sum + (b.wadarta || 0), 0);
    const totalBadDebt = bills.reduce((sum, b) => sum + (b.bad_debt || 0), 0);
    const totalDiscount = bills.reduce((sum, b) => sum + (b.discount || 0), 0);
    const totalRc = totalWadarta - totalPaid - totalBadDebt - totalDiscount;
    const totalBilledCustomers = bills.filter(b => b.billGeneratedFlag === 'Y').length;

    return {
      totalCustomers: bills.length,
      totalBilledCustomers,
      totalBillAmount,
      totalPaid,
      totalRh,
      totalWadarta,
      totalRc,
      totalBadDebt,
      totalDiscount
    };
  }

  // ---------- interactions ----------
  setViewMode(mode: 'summary' | 'employee'): void {
    this.viewMode = mode;
    this.buildChart();
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
    this.buildChart();
  }

  toggleMetric(metric: MetricConfig): void {
    metric.selected = !metric.selected;
    this.buildChart();
  }

  onEmployeeChange(employeeId: number): void {
    this.selectedEmployeeId = Number(employeeId);
    this.buildChart();
  }

  get selectedEmployeeName(): string {
    const emp = this.collectorEmployees.find(e => e.employeeId === this.selectedEmployeeId);
    return emp ? (emp.employeeName || `Employee ${emp.employeeId}`) : '';
  }

  get selectedMetrics(): MetricConfig[] {
    return this.metrics.filter(m => m.selected);
  }

  get latestCycle(): CycleData | null {
    return this.cycles.length ? this.cycles[this.cycles.length - 1] : null;
  }

  get previousCycle(): CycleData | null {
    return this.cycles.length > 1 ? this.cycles[this.cycles.length - 2] : null;
  }

  getCurrentMetrics(cycle: CycleData): CycleMetrics {
    if (this.viewMode === 'summary') {
      return cycle.summary;
    }
    return cycle.byEmployee.get(this.selectedEmployeeId as number) || this.emptyMetrics();
  }

  getKpiValue(metric: MetricConfig): string {
    const cycle = this.latestCycle;
    if (!cycle) { return '—'; }
    const value = this.getCurrentMetrics(cycle)[metric.key];
    return metric.type === 'count' ? this.formatCount(value) : this.formatCurrency(value);
  }

  getKpiTrend(metric: MetricConfig): { direction: 'up' | 'down' | 'flat'; percent: string } {
    const latest = this.latestCycle;
    const prev = this.previousCycle;
    if (!latest || !prev) { return { direction: 'flat', percent: '' }; }
    const cur = this.getCurrentMetrics(latest)[metric.key];
    const before = this.getCurrentMetrics(prev)[metric.key];
    if (!before) { return { direction: 'flat', percent: '' }; }
    const change = ((cur - before) / Math.abs(before)) * 100;
    if (Math.abs(change) < 0.05) { return { direction: 'flat', percent: '0.0%' }; }
    return {
      direction: change > 0 ? 'up' : 'down',
      percent: `${Math.abs(change).toFixed(1)}%`
    };
  }

  // ---------- chart building ----------
  buildChart(): void {
    if (!this.dataLoaded || !this.cycles.length) {
      this.chartSeries = [];
      this.barGroups = [];
      return;
    }

    const selected = this.selectedMetrics;
    const plotW = this.chartWidth - this.chartPadding.left - this.chartPadding.right;
    const plotH = this.chartHeight - this.chartPadding.top - this.chartPadding.bottom;
    const n = this.cycles.length;

    // Value range across all selected metrics
    let minVal = 0;
    let maxVal = 0;
    selected.forEach(metric => {
      this.cycles.forEach(cycle => {
        const v = this.getCurrentMetrics(cycle)[metric.key];
        if (v > maxVal) { maxVal = v; }
        if (v < minVal) { minVal = v; }
      });
    });
    if (maxVal === minVal) { maxVal = minVal + 1; }
    const range = maxVal - minVal;
    maxVal = maxVal + range * 0.08;
    if (minVal < 0) { minVal = minVal - range * 0.08; }

    const xFor = (i: number) => this.chartPadding.left + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
    const yFor = (v: number) => this.chartPadding.top + plotH - ((v - minVal) / (maxVal - minVal)) * plotH;

    // X ticks
    this.xTicks = this.cycles.map((c, i) => ({ x: xFor(i), label: c.shortLabel }));

    // Y ticks (5 divisions)
    this.yTicks = [];
    const tickCount = 5;
    for (let t = 0; t <= tickCount; t++) {
      const v = minVal + ((maxVal - minVal) * t) / tickCount;
      this.yTicks.push({ y: yFor(v), label: this.formatAxisValue(v) });
    }

    if (this.chartType === 'line') {
      this.barGroups = [];
      this.chartSeries = selected.map(metric => {
        const points = this.cycles.map((cycle, i) => {
          const value = this.getCurrentMetrics(cycle)[metric.key];
          return { x: xFor(i), y: yFor(value), value, cycleLabel: cycle.label };
        });
        const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
        const baseline = yFor(Math.max(0, minVal));
        const areaPath = points.length
          ? `${path} L ${points[points.length - 1].x.toFixed(1)} ${baseline.toFixed(1)} L ${points[0].x.toFixed(1)} ${baseline.toFixed(1)} Z`
          : '';
        return {
          key: metric.key,
          label: metric.label,
          color: metric.color,
          type: metric.type,
          points,
          path,
          areaPath
        };
      });
    } else {
      this.chartSeries = [];
      const groupWidth = plotW / n;
      const barAreaWidth = groupWidth * 0.7;
      const barWidth = selected.length ? Math.min(34, barAreaWidth / selected.length) : 0;
      const baseline = yFor(Math.max(0, minVal));

      this.barGroups = this.cycles.map((cycle, i) => {
        const groupCenter = this.chartPadding.left + groupWidth * i + groupWidth / 2;
        const startX = groupCenter - (barWidth * selected.length) / 2;
        const bars = selected.map((metric, mi) => {
          const value = this.getCurrentMetrics(cycle)[metric.key];
          const y = yFor(value);
          const top = Math.min(y, baseline);
          const height = Math.max(1, Math.abs(baseline - y));
          return {
            x: startX + mi * barWidth,
            y: top,
            height,
            width: Math.max(2, barWidth - 3),
            color: metric.color,
            label: metric.label,
            value,
            type: metric.type
          };
        });
        return { x: groupCenter, cycleLabel: cycle.label, bars };
      });
    }
  }

  // ---------- hover tooltip ----------
  onChartMouseMove(event: MouseEvent): void {
    if (!this.cycles.length) { return; }
    const svg = event.currentTarget as SVGElement;
    const rect = svg.getBoundingClientRect();
    const scaleX = this.chartWidth / rect.width;
    const mouseX = (event.clientX - rect.left) * scaleX;

    const plotW = this.chartWidth - this.chartPadding.left - this.chartPadding.right;
    const n = this.cycles.length;
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < n; i++) {
      const x = this.chartPadding.left + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
      const dist = Math.abs(mouseX - x);
      if (dist < minDist) { minDist = dist; closest = i; }
    }
    this.hoverIndex = closest;
    this.hoverX = this.chartPadding.left + (n === 1 ? plotW / 2 : (closest / (n - 1)) * plotW);
  }

  onChartMouseLeave(): void {
    this.hoverIndex = null;
  }

  get hoverCycle(): CycleData | null {
    return this.hoverIndex !== null && this.cycles[this.hoverIndex] ? this.cycles[this.hoverIndex] : null;
  }

  getHoverValue(metric: MetricConfig): string {
    const cycle = this.hoverCycle;
    if (!cycle) { return ''; }
    const value = this.getCurrentMetrics(cycle)[metric.key];
    return metric.type === 'count' ? this.formatCount(value) : this.formatCurrency(value);
  }

  get tooltipX(): number {
    // Keep the tooltip inside the chart
    const tooltipWidth = 230;
    if (this.hoverX + 16 + tooltipWidth > this.chartWidth - this.chartPadding.right) {
      return this.hoverX - tooltipWidth - 16;
    }
    return this.hoverX + 16;
  }

  // ---------- table ----------
  getTableValue(cycle: CycleData, metric: MetricConfig): string {
    const value = this.getCurrentMetrics(cycle)[metric.key];
    return metric.type === 'count' ? this.formatCount(value) : this.formatCurrency(value);
  }

  // ---------- formatting ----------
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  }

  formatCount(value: number): string {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  formatAxisValue(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1000000) { return `${(value / 1000000).toFixed(1)}M`; }
    if (abs >= 1000) { return `${(value / 1000).toFixed(1)}K`; }
    return value.toFixed(0);
  }
}
